import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageIcon, Volume2, Plus } from "lucide-react";

export default function AdminQuestions() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const nav = useNavigate();

  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)); }, []);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat && cat !== "all") params.set("category_code", cat);
    api.get(`/admin/questions?${params.toString()}`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, cat]);

  return (
    <AdminLayout title="Questions" actions={
      <div className="flex gap-2">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-56 bg-white/5 border-white/15 text-white" data-testid="cat-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.name_pt}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Search question text..." value={q} onChange={(e) => setQ(e.target.value)} className="w-72 bg-white/5 border-white/15 text-white" data-testid="question-search" />
        <Button onClick={() => nav("/admin/questions/new")} className="bg-[hsl(var(--accent))] text-black hover:bg-[hsl(var(--accent))]/90 shrink-0" data-testid="new-question-btn">
          <Plus className="w-4 h-4 mr-1" /> New question
        </Button>
      </div>
    }>
      <div className="bg-white/4 border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-2 border-b border-white/8 text-xs font-mono uppercase tracking-widest text-white/50">{items.length} results</div>
        <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
          <table className="w-full text-sm" data-testid="questions-table">
            <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/50 font-mono sticky top-0">
              <tr>
                <th className="text-left px-4 py-3 w-16">#</th>
                <th className="text-left px-4 py-3 w-20">Cat</th>
                <th className="text-left px-4 py-3">Question</th>
                <th className="text-center px-4 py-3 w-16">Correct</th>
                <th className="text-center px-4 py-3 w-16">Media</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} onClick={() => nav(`/admin/questions/${it.id}`)}
                  className="border-t border-white/8 hover:bg-white/8 cursor-pointer" data-testid={`q-row-${it.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-white/50">{it.number}</td>
                  <td className="px-4 py-3 font-mono text-xs">{it.category_code}</td>
                  <td className="px-4 py-3 truncate max-w-md">{it.text_pt}</td>
                  <td className="px-4 py-3 text-center"><span className="font-mono text-xs bg-white/8 rounded px-1.5 py-0.5">{it.correct_letter || "-"}</span></td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center gap-1.5">
                      {it.image_file && <ImageIcon className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />}
                      {it.audio_file && <Volume2 className="w-3.5 h-3.5 text-[hsl(var(--accent))]" />}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-white/40">No questions.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
